import { Action, createHook, createStore } from "react-sweet-state";

import {
  authenticatedGetFetch,
  authenticatedPostFetch,
} from "utility/authenticated_fetch";
import { captureSentryException } from "utility/capture_sentry_exception";

// -----------------------------------------------------------------------------

export enum SlackProgress {
  Unknown,
  Submitting,
  ErrorOAuthParam,
  Error,
  Success,
}

// -----------------------------------------------------------------------------

const initialState: SlackStoreState = {
  progress: SlackProgress.Unknown,
  listProgress: SlackProgress.Unknown,
  channels: {},
};

// -----------------------------------------------------------------------------

export const useSlackStore = createHook(
  createStore({
    initialState,
    actions: {
      submitParams,
      listChannels,
    },
  })
);

// -----------------------------------------------------------------------------

function submitParams(
  oauthCode: string | null,
  oauthState: string | null
): Action<SlackStoreState> {
  return async ({ setState }) => {
    if (!oauthCode) {
      return setState({
        progress: SlackProgress.ErrorOAuthParam,
        errorDetail: "code is missing or empty",
      });
    }
    if (!oauthState) {
      return setState({
        progress: SlackProgress.ErrorOAuthParam,
        errorDetail: "state is missing or empty",
      });
    }

    //
    try {
      setState({ progress: SlackProgress.Submitting });
      await authenticatedPostFetch("api/organization/slack", {
        code: oauthCode,
        state: oauthState,
      });
      setState({
        progress: SlackProgress.Success,
        errorDetail: undefined,
      });
    } catch (error) {
      setState({
        progress: SlackProgress.Error,
        errorDetail: (error as Error)?.message ?? "There was an error",
      });
    }
  };
}

function listChannels(): Action<SlackStoreState> {
  return async ({ setState }) => {
    try {
      setState({ listProgress: SlackProgress.Submitting });
      const responsePayload = await authenticatedGetFetch(
        "lookup/slack/channels"
      );
      setState({
        listProgress: SlackProgress.Success,
        channels: responsePayload.channels,
      });
    } catch (error) {
      captureSentryException(error, "Failed to list slack channels");
      setState({ listProgress: SlackProgress.Error });
    }
  };
}

// -----------------------------------------------------------------------------

interface SlackStoreState {
  progress: SlackProgress;
  errorDetail?: string;
  listProgress: SlackProgress;
  channels: Channels;
}

interface Channels {
  [connection_uuid: string]: string[][];
}
